// React
import React, { Fragment, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import { HostStyles, Container, Title } from './styles'
import Service from './Service'

// Shared
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import { withTranslation } from 'shared/utils/withTranslation'
import ItemActions from 'back_office/v2/redux/actions/item_actions'
import LineActions from 'back_office/v2/redux/actions/line_actions'

const Services = ({ cartId, services, itemIds, fetchLines, fetchServices, title, t }) => {
  useEffect(() => {
    if (!cartId) return

    // We need to fetch both services and lines
    // because we need to know which services are already added to the cart
    fetchLines(cartId).then(() => {
      fetchServices()
    })
  }, [cartId])

  const isServiceInCart = useCallback((serviceId) => itemIds.includes(serviceId), [itemIds])

  if (!services.size()) return null

  return (
    <Fragment>
      <HostStyles />
      <Container>
        <Title>{title || t('user.store.additional_services')}</Title>
        {services?.map((service) => {
          const inCart = isServiceInCart(service.id)

          return <Service key={service.id} service={service} inCart={inCart} />
        })}
      </Container>
    </Fragment>
  )
}

Services.propTypes = {
  cartId: PropTypes.string,
  services: PropTypes.object,
  itemIds: PropTypes.object,
  fetchLines: PropTypes.func,
  fetchServices: PropTypes.func,
  t: PropTypes.func,
  // Element attributes
  title: PropTypes.any
}

const mapStateToProps = (state, _props) => {
  const cartId = state.client.cartId
  const cart = state.orm.carts.getById(cartId)

  const lines = state.orm.lines.getAll({ id: cart?.lines })
  const itemIds = lines.map((line) => line.item_id)

  const enabledServices = state.orm.product_groups.getAll({
    product_type: 'service',
    show_in_store: true,
    archived_at: null
  })
  const services = state.orm.products
    .getAll({ product_type: 'service', product_group_id: enabledServices.pluck('id') })
    .sortBy((product) => enabledServices.getById(product.product_group_id)?.sorting_weight)

  return { cartId, services, itemIds }
}

const mapDispatchToProps = (_dispatch, _props) => {
  return {
    fetchLines: (id) => {
      return LineActions.fetchAll({
        apiVersion: 'boomerang',
        include: [
          'item',
          'item.photo',
          'item.bundle_items',
          'item.bundle_items.product',
          'item.bundle_items.product.photo'
        ],
        filter: {
          owner_id: id
        }
      })
    },
    fetchServices: () => {
      return ItemActions.fetchAll({
        apiVersion: 'boomerang',
        filter: { services: true, archived: false, show_in_store: true },
        page: {
          number: 1,
          size: 100
        }
      })
    }
  }
}

const decoratedComponent = compose(
  withStore,
  withTheme,
  withTranslation('user'),
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(Services)

registerCustomElement('bq-cart-services', decoratedComponent, {
  attributes: ['title']
})

export default decoratedComponent
